import React, { FC } from "react";
import { useI18n } from "../../hooks/useTranslations";
import { CustomError } from "../../http/errors";
import { ProcessingRequestFinalItem } from "../../http/processingRequestFinalItem/types";
import { Paginated } from "../../types";
import { formatPrice } from "../../utils";
import Box from "../ui/Box";
import ContentContainer from "../ui/ContentContainer";
import Inline from "../ui/Inline";
import Table from "../ui/Table";
import styles from "./UsedProducts.module.css";

type UsedProductsProps = {
  finalItems: Paginated<ProcessingRequestFinalItem> | undefined;
  totalUsedProductsCost: number;
  finalItemsError: CustomError | undefined;
  isKit: boolean;
};

const UsedProducts: FC<UsedProductsProps> = ({ finalItems, totalUsedProductsCost, finalItemsError, isKit }) => {
  const i18n = useI18n();

  return (
    <Box
      fullHeight
      title={isKit ? i18n.t.common.usedProductsFor + finalItems?.items[0]?.item.code : i18n.t.common.usedProducts}
      subtitle={
        isKit && (
          <Inline columnGap={1} justifyContent="space-around">
            <div>
              <strong>{i18n.t.costObjects.singular}</strong>
              <div>{finalItems?.items[0]?.item.costObject || "---"}</div>
            </div>
            <div>
              <strong>{i18n.t.common.quantity}</strong>
              <div>
                {finalItems?.items[0]?.quantity
                  ? `${finalItems?.items[0]?.quantity} ${finalItems?.items[0]?.item.unitOfMeasure}`
                  : "---"}
              </div>
            </div>
          </Inline>
        )
      }
      type="secondary"
      isScrollableBody
    >
      {isKit && (
        <>
          <ContentContainer>
            <Table
              head={
                <tr>
                  <th align="left">{i18n.t.common.code}</th>
                  <th align="left">{i18n.t.costObjects.singular}</th>
                  <th align="right">{i18n.t.common.quantity}</th>
                  <th align="right">{i18n.t.common.costPerUnit}</th>
                  <th align="right">{i18n.t.common.totalCost}</th>
                </tr>
              }
              renderRow={(initialItem) => (
                <>
                  <td>{initialItem.item.code}</td>
                  <td>{initialItem.item.costObject ?? "---"}</td>
                  <td align="right">
                    {initialItem.quantity} {initialItem.item?.unitOfMeasure || i18n.t.common.pz}
                  </td>
                  <td align="right">{formatPrice(initialItem.cost, i18n.language)}</td>
                  <td align="right">{formatPrice(initialItem.cost * initialItem.quantity, i18n.language)}</td>
                </>
              )}
              footer={
                <tr>
                  <td colSpan={3}>{i18n.t.common.totalCost}:</td>
                  <td align="right" colSpan={2}>
                    {formatPrice(totalUsedProductsCost, i18n.language)}
                  </td>
                </tr>
              }
              items={finalItems?.items[0]?.initialItems}
              error={finalItemsError}
            />
          </ContentContainer>
        </>
      )}
      {finalItems && !isKit && (
        <ContentContainer>
          <Table
            head={
              <>
                <tr>
                  <th colSpan={5}>{i18n.t.common.initialItem}</th>
                  <th colSpan={5}>{i18n.t.common.finalItem}</th>
                </tr>
                <tr>
                  <th>{i18n.t.common.code}</th>
                  <th>{i18n.t.costObjects.singular}</th>
                  <th>{i18n.t.common.quantity}</th>
                  <th>{i18n.t.common.costPerUnit}</th>
                  <th className={styles.borderRight}>{i18n.t.common.totalCost}</th>
                  <th>{i18n.t.common.code}</th>
                  <th>{i18n.t.costObjects.singular}</th>
                  <th>{i18n.t.common.quantity}</th>
                  <th>{i18n.t.common.costPerUnit}</th>
                  <th>{i18n.t.common.totalCost}</th>
                </tr>
              </>
            }
            renderRow={(finalItem) => (
              <>
                {finalItem.initialItems.map((initialItem, i) => (
                  <React.Fragment key={i}>
                    <td>{initialItem.item.code}</td>
                    <td align="center">{initialItem.item.costObject ?? "---"}</td>
                    <td align="center">
                      {initialItem.quantity} {initialItem.item.unitOfMeasure || i18n.t.common.pz}
                    </td>
                    <td align="center">{formatPrice(initialItem.cost, i18n.language)}</td>
                    <td align="center" className={styles.borderRight}>
                      {formatPrice(initialItem.cost * initialItem.quantity, i18n.language)}
                    </td>
                  </React.Fragment>
                ))}

                <td>{finalItem.item.code}</td>
                <td align="center">{finalItem.item.costObject ?? "---"}</td>
                <td align="center">
                  {finalItem.quantity} {finalItem.item.unitOfMeasure || i18n.t.common.pz}
                </td>
                <td align="center">{formatPrice(finalItem.cost, i18n.language)}</td>
                <td align="center">{formatPrice(finalItem.cost * finalItem.quantity, i18n.language)}</td>
              </>
            )}
            footer={
              <tr>
                <td colSpan={4}>{i18n.t.common.totalCost}:</td>
                <td align="center" colSpan={1}>
                  {formatPrice(totalUsedProductsCost, i18n.language)}
                </td>
              </tr>
            }
            items={finalItems?.items}
            error={finalItemsError}
          />
        </ContentContainer>
      )}
    </Box>
  );
};

export default UsedProducts;
